import { useState, useEffect } from 'react';
import { Card, Button, Badge } from '@atoms/index';
import { AlertModal } from '@/components/organisms/AlertModal';
import { useDashboardMetrics, useTrends, useKPIs } from '@/hooks/useAnalytics';
import { TrendChart } from '@organisms/TrendChart';
import { formatCurrency } from '@/utils/currency';
import { formatPrice } from '@/utils/format';
import { analyticsService, AnalyticsPeriod } from '@services/analyticsService';
import { cn } from '@/utils/cn';
import { exportToExcel } from '@/utils/excelExport';

export default function AnalyticsTab() {
  const [selectedPeriod, setSelectedPeriod] = useState<AnalyticsPeriod>('today');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [isCustomPeriod, setIsCustomPeriod] = useState(false);
  const [alertState, setAlertState] = useState<{ isOpen: boolean; title: string; message: string }>({ isOpen: false, title: '', message: '' });

  const dashboardMetrics = useDashboardMetrics(
    isCustomPeriod ? 'custom' : selectedPeriod,
    isCustomPeriod ? startDate : undefined,
    isCustomPeriod ? endDate : undefined,
  );

  const trends = useTrends(
    isCustomPeriod ? 'custom' : selectedPeriod,
    isCustomPeriod ? startDate : undefined,
    isCustomPeriod ? endDate : undefined,
  );

  const kpis = useKPIs();

  const handlePeriodChange = (period: AnalyticsPeriod) => {
    setSelectedPeriod(period);
    setIsCustomPeriod(period === 'custom');
    if (period !== 'custom') {
      setStartDate('');
      setEndDate('');
    }
  };

  const handleExportReport = async (type: 'financial' | 'operational' | 'customer' | 'restaurant' | 'courier') => {
    try {
      const report = await analyticsService.generateReport({
        type,
        startDate: isCustomPeriod ? startDate : new Date().toISOString().split('T')[0],
        endDate: isCustomPeriod ? endDate : new Date().toISOString().split('T')[0],
        format: 'json',
      });

      const reportData = prepareReportDataForExcel(type, report, isCustomPeriod ? startDate : undefined, isCustomPeriod ? endDate : undefined);
      exportToExcel(reportData);
    } catch (error) {
      console.error('Export failed:', error);
      setAlertState({ isOpen: true, title: 'Hata', message: 'Rapor export edilemedi' });
    }
  };

  const prepareReportDataForExcel = (
    type: 'financial' | 'operational' | 'customer' | 'restaurant' | 'courier',
    report: any,
    startDate?: string,
    endDate?: string
  ) => {
    const dateStr = startDate && endDate ? `${startDate}_${endDate}` : new Date().toISOString().split('T')[0];
    
    switch (type) {
      case 'financial': {
        const periodStart = report.period?.start ? new Date(report.period.start).toLocaleDateString('tr-TR') : '';
        const periodEnd = report.period?.end ? new Date(report.period.end).toLocaleDateString('tr-TR') : '';
        return {
          filename: `finansal-rapor-${dateStr}`,
          headers: ['Metrik', 'Değer (TL)', 'Açıklama'],
          data: [
            ['Dönem Başlangıcı', periodStart, ''],
            ['Dönem Bitişi', periodEnd, ''],
            ['Toplam Gelir', formatCurrency(report.totalRevenue || 0), 'Tüm siparişlerden elde edilen toplam gelir'],
            ['Komisyon Geliri', formatCurrency(report.commissionRevenue || 0), 'Siparişlerden platform tarafından alınan komisyon geliri'],
            ['Teslimat Ücretleri', formatCurrency(report.deliveryFees || 0), 'Müşterilerden alınan teslimat ücretleri'],
            ['Net Kar', formatCurrency(report.netProfit || 0), 'Komisyon geliri (platform karı)'],
          ],
        };
      }
      case 'operational': {
        const periodStart = report.period?.start ? new Date(report.period.start).toLocaleDateString('tr-TR') : '';
        const periodEnd = report.period?.end ? new Date(report.period.end).toLocaleDateString('tr-TR') : '';
        const statusMap: Record<string, string> = {
          CREATED: 'Oluşturuldu',
          PREPARING: 'Hazırlanıyor',
          READY: 'Hazır',
          ON_WAY: 'Yolda',
          DELIVERED: 'Teslim Edildi',
          CANCELLED: 'İptal Edildi',
        };
        const rows: any[][] = [
          ['Dönem Başlangıcı', periodStart, ''],
          ['Dönem Bitişi', periodEnd, ''],
          ['Ortalama Teslimat Süresi', `${Math.round(report.avgDeliveryTimeMinutes || 0)} dakika`, ''],
          ['', '', ''],
          ['Sipariş Durumu', 'Adet', ''],
        ];
        if (report.ordersByStatus) {
          report.ordersByStatus.forEach((item: any) => {
            rows.push([statusMap[item.status] || item.status, item.count, '']);
          });
        }
        return {
          filename: `operasyonel-rapor-${dateStr}`,
          headers: ['Metrik', 'Değer', 'Açıklama'],
          data: rows,
        };
      }
      case 'customer': {
        const periodStart = report.period?.start ? new Date(report.period.start).toLocaleDateString('tr-TR') : '';
        const periodEnd = report.period?.end ? new Date(report.period.end).toLocaleDateString('tr-TR') : '';
        return {
          filename: `musteri-raporu-${dateStr}`,
          headers: ['Metrik', 'Değer', 'Açıklama'],
          data: [
            ['Dönem Başlangıcı', periodStart, ''],
            ['Dönem Bitişi', periodEnd, ''],
            ['Yeni Müşteriler', report.newCustomers || 0, 'Dönem içinde kayıt olan müşteri sayısı'],
            ['Aktif Müşteriler', report.activeCustomers || 0, 'Dönem içinde sipariş veren müşteri sayısı'],
          ],
        };
      }
      case 'restaurant': {
        const periodStart = report.period?.start ? new Date(report.period.start).toLocaleDateString('tr-TR') : '';
        const periodEnd = report.period?.end ? new Date(report.period.end).toLocaleDateString('tr-TR') : '';
        const rows: any[][] = [
          ['Dönem Başlangıcı', periodStart, ''],
          ['Dönem Bitişi', periodEnd, ''],
          ['', '', ''],
          ['Restoran Adı', 'Sipariş Sayısı', 'Gelir (TL)'],
        ];
        if (report.topRestaurants) {
          report.topRestaurants.forEach((restaurant: any) => {
            rows.push([restaurant.name || 'Bilinmeyen', restaurant.orderCount || 0, formatCurrency(restaurant.revenue || 0)]);
          });
        }
        return {
          filename: `restoran-raporu-${dateStr}`,
          headers: ['Metrik', 'Değer', 'Açıklama'],
          data: rows,
        };
      }
      case 'courier': {
        const periodStart = report.period?.start ? new Date(report.period.start).toLocaleDateString('tr-TR') : '';
        const periodEnd = report.period?.end ? new Date(report.period.end).toLocaleDateString('tr-TR') : '';
        const rows: any[][] = [
          ['Dönem Başlangıcı', periodStart, ''],
          ['Dönem Bitişi', periodEnd, ''],
          ['', '', ''],
          ['Kurye Adı', 'Teslimat Sayısı', 'Ortalama Teslimat Süresi (dakika)'],
        ];
        if (report.topCouriers) {
          report.topCouriers.forEach((courier: any) => {
            rows.push([
              courier.name || 'Bilinmeyen',
              courier.deliveryCount || 0,
              Math.round(courier.avgDeliveryTimeMinutes || 0),
            ]);
          });
        }
        return {
          filename: `kurye-raporu-${dateStr}`,
          headers: ['Metrik', 'Değer', 'Açıklama'],
          data: rows,
        };
      }
      default:
        return {
          filename: `rapor-${dateStr}`,
          headers: ['Veri'],
          data: [['Rapor verisi bulunamadı']],
        };
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Platform Analitiği</h2>
          <p className="text-gray-600">Platform geneli metrikler ve trendler</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleExportReport('financial')}
          >
            📊 Finansal Rapor
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleExportReport('operational')}
          >
            ⚙️ Operasyonel Rapor
          </Button>
        </div>
      </div>

      <Card>
        <div className="flex flex-wrap gap-3 mb-4">
          <button
            onClick={() => handlePeriodChange('today')}
            className={cn(
              'px-4 py-2 rounded-lg font-medium transition-colors',
              selectedPeriod === 'today' && !isCustomPeriod
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
            )}
          >
            Bugün
          </button>
          <button
            onClick={() => handlePeriodChange('week')}
            className={cn(
              'px-4 py-2 rounded-lg font-medium transition-colors',
              selectedPeriod === 'week' && !isCustomPeriod
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
            )}
          >
            Bu Hafta
          </button>
          <button
            onClick={() => handlePeriodChange('month')}
            className={cn(
              'px-4 py-2 rounded-lg font-medium transition-colors',
              selectedPeriod === 'month' && !isCustomPeriod
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
            )}
          >
            Bu Ay
          </button>
          <button
            onClick={() => handlePeriodChange('custom')}
            className={cn(
              'px-4 py-2 rounded-lg font-medium transition-colors',
              isCustomPeriod
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
            )}
          >
            Özel Tarih
          </button>
        </div>

        {isCustomPeriod && (
          <div className="flex gap-4 mb-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Başlangıç Tarihi</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-5 py-3 border border-gray-200 rounded-2xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary hover:border-gray-300 bg-white"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Bitiş Tarihi</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-5 py-3 border border-gray-200 rounded-2xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary hover:border-gray-300 bg-white"
              />
            </div>
          </div>
        )}
      </Card>

      {dashboardMetrics.isLoading && (
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-gray-600">Metrikler yükleniyor...</p>
        </div>
      )}

      {dashboardMetrics.error && (
        <Card className="bg-warning/10 border border-warning/20">
          <p className="text-warning">{dashboardMetrics.error}</p>
        </Card>
      )}

      {!dashboardMetrics.isLoading && !dashboardMetrics.error && dashboardMetrics.data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <div className="text-center">
                <p className="text-3xl font-bold text-primary">{dashboardMetrics.data.totalOrders}</p>
                <p className="text-sm text-gray-600">Toplam Sipariş</p>
                {dashboardMetrics.data.growthRate !== 0 && (
                  <p
                    className={cn(
                      'text-xs mt-1',
                      dashboardMetrics.data.growthRate > 0 ? 'text-success' : 'text-danger',
                    )}
                  >
                    {dashboardMetrics.data.growthRate > 0 ? '↑' : '↓'}{' '}
                    {Math.abs(dashboardMetrics.data.growthRate).toFixed(1)}%
                  </p>
                )}
              </div>
            </Card>

            <Card>
              <div className="text-center">
                <p className="text-3xl font-bold text-success">
                  {formatCurrency(dashboardMetrics.data.totalRevenue)}
                </p>
                <p className="text-sm text-gray-600">Toplam Gelir</p>
              </div>
            </Card>

            <Card>
              <div className="text-center">
                <p className="text-3xl font-bold text-warning">
                  {formatCurrency(dashboardMetrics.data.avgOrderValue)}
                </p>
                <p className="text-sm text-gray-600">Ortalama Sipariş Değeri</p>
              </div>
            </Card>

            <Card>
              <div className="text-center">
                <p className="text-3xl font-bold text-info">{dashboardMetrics.data.activeUsers}</p>
                <p className="text-sm text-gray-600">Aktif Kullanıcılar</p>
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <div className="text-center">
                <p className="text-2xl font-bold text-primary">{dashboardMetrics.data.activeRestaurants}</p>
                <p className="text-sm text-gray-600">Aktif Restoranlar</p>
              </div>
            </Card>

            <Card>
              <div className="text-center">
                <p className="text-2xl font-bold text-secondary">{dashboardMetrics.data.activeCouriers}</p>
                <p className="text-sm text-gray-600">Aktif Kuryeler</p>
              </div>
            </Card>

            <Card>
              <div className="text-center">
                <p className="text-2xl font-bold text-warning">
                  {dashboardMetrics.data.growthRate > 0 ? '+' : ''}
                  {dashboardMetrics.data.growthRate.toFixed(1)}%
                </p>
                <p className="text-sm text-gray-600">Büyüme Oranı</p>
              </div>
            </Card>
          </div>
        </>
      )}

      {kpis.data && (
        <Card>
          <h3 className="text-lg font-bold text-gray-900 mb-4">KPI Karşılaştırması</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-2">Bugün</p>
              <p className="text-2xl font-bold text-primary">{kpis.data.today.orders}</p>
              <p className="text-sm text-gray-500">Sipariş</p>
              <p className="text-lg font-semibold text-success mt-2">
                {formatCurrency(kpis.data.today.revenue)}
              </p>
              <p className="text-xs text-gray-500">Gelir</p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-2">Dün</p>
              <p className="text-2xl font-bold text-primary">{kpis.data.yesterday.orders}</p>
              <p className="text-sm text-gray-500">Sipariş</p>
              <p className="text-lg font-semibold text-success mt-2">
                {formatCurrency(kpis.data.yesterday.revenue)}
              </p>
              <p className="text-xs text-gray-500">Gelir</p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-2">Geçen Hafta</p>
              <p className="text-2xl font-bold text-primary">{kpis.data.lastWeek.orders}</p>
              <p className="text-sm text-gray-500">Sipariş</p>
              <p className="text-lg font-semibold text-success mt-2">
                {formatCurrency(kpis.data.lastWeek.revenue)}
              </p>
              <p className="text-xs text-gray-500">Gelir</p>
            </div>
          </div>
        </Card>
      )}

      <Card>
        <h3 className="text-lg font-bold text-gray-900 mb-4">Trend Grafiği</h3>
        {trends.isLoading && (
          <div className="h-64 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        )}
        {trends.error && (
          <div className="h-64 flex items-center justify-center text-warning">{trends.error}</div>
        )}
        {!trends.isLoading && !trends.error && (
          <TrendChart data={trends.data} period={selectedPeriod === 'custom' ? 'week' : selectedPeriod} />
        )}
      </Card>

      <AlertModal
        isOpen={alertState.isOpen}
        title={alertState.title}
        message={alertState.message}
        variant="danger"
        onClose={() => setAlertState((a) => ({ ...a, isOpen: false }))}
      />
    </div>
  );
}
